import { Module } from 'vuex';
import { Route } from 'vue-router';
import { Position } from 'codemirror';

import { RootState, Optional, Nullable, unreachableCase, GlobalState } from 'frontend/types';
import {
  BLANK_LESSON_SOURCE,
  CompiledElmGetter,
  ContentHashes,
  EditorStatusTuple,
  ElmFileDict,
  LessonAsset,
  LessonAutosave,
  LessonSourceObject,
  LessonWidgetSchema,
  OptionsLocalStorage,
  Package,
  SidebarTab,
  TranspilerResponse,
  UsedAsset,
  isJSONConfigAsset,
} from 'frontend/types/lessons/editor';
import { Lesson, LessonState } from 'frontend/types/lessons/lesson';
import {
  LessonAssetActionInputs,
  LessonCreateApiInputs,
  LessonCreateApiResult,
  LessonCreateAutosaveApiInputs,
  LessonCreateAutosaveApiResult,
  LessonDraftCreateApiInputs,
  LessonDraftCreateApiResult,
  LessonDraftUpdateApiInputs,
  LessonDraftUpdateApiResult,
  LessonEditHistoryInputs,
  LessonEditHistoryResult,
  LessonFeedbackLoadApiInputs,
  LessonFeedbackLoadApiResult,
  LessonPublishApiInputs,
  LessonPublishApiResult,
  LessonReportLoadApiInputs,
  LessonReportLoadApiResult,
  LessonRetireVersionInputs,
  LessonRetireVersionResult,
  LessonUploadAssetsInputs,
  LessonUploadAssetsResult,
  LessonVersionsApiInputs,
  LessonVersionsApiResult,
  ErrorsDict,
} from 'frontend/types/lessons/requests';
import { getData } from 'frontend/utils/ajax';
import { apiResolve } from 'frontend/router';
import { ElmerError } from 'frontend/types/elmer';
import { deepCopyObj } from 'frontend/utils/copy';
import { apiRequest, apiRequestWithErrors } from 'frontend/store/modules/lessons/apiHelpers';
import { mainModuleName, elmFilesForElmer } from 'frontend/views/lessons/editor/elmFileInfo';
import {
  saveOptionsToLocalStorage,
  getOptionsFromLocalStorage,
} from 'frontend/views/lessons/editor/optionsLocalStorage';
import {
  AssetsPanelEditorState,
  AssetsPanelStateChange,
  getCodePanelDefaultState,
  CodePanelEditorState,
  CodePanelStateChange,
  EditorFoldAdjustment,
  EditorFoldChange,
  EditorState,
  EditorStateAction,
  getCamperdownSourceDefaultState,
} from 'frontend/types/lessons/editorState';
import {
  AssetWarningCountsGetter,
  EditorWarningPayload,
  EditorWarnings,
  EditorWarningGetter,
  TranspilerWarning,
  TranspilerWarningGetter,
  Severity,
  WarningCountsGetter,
  WarningInputSource,
  editorWarningToPlainText,
} from 'frontend/types/lessons/warnings';
import { shouldWarningBlockPublishing } from 'frontend/views/lessons/editor/warningUtils';
import { toUniqueAssetAlias } from 'frontend/views/lessons/editor/utils';
import { InteractivesManifest } from 'visualizations/shared/interactivesManager/types';

interface LessonEditorState extends ErrorsDict {
  lessonIsSaving: boolean;
  lessonIsAutosaving: boolean;
  requestErrors: string[];
  elmerErrors: Nullable<ElmerError>;
  autosaveOutdated: boolean;
  mutableLessonSource: LessonSourceObject;
  transpilerSha: string;
  editorWasDeployed: boolean;
  status: Nullable<EditorStatusTuple>;
  editorOptions: Nullable<OptionsLocalStorage>;
  editorWarnings: EditorWarnings;
  forcedReadOnly: boolean;

  // Compilation data
  mainElm: string;
  additionalElmMains: string[];
  additionalElm: ElmFileDict;
  packages: Package[];
  contentHashes: Nullable<ContentHashes>;
  lessonWidgetSchema: LessonWidgetSchema[];
  unifiedTranspilerWarnings: TranspilerWarning[];
  interactivesManifest: InteractivesManifest;
  // This is any because the type exists in brilliant-next
  camperdownAsPractice: any;
  usedAssets: UsedAsset[];

  // Editor state
  editorState: EditorState;

  // AssetFormWrapper state
  assetPreviewWidth: Nullable<string>;

  // Ownership change
  ownerChanged: boolean;
  newOwnerName: Nullable<string>;

  // Lesson state change (to non-editable state)
  lessonStateChanged: boolean;
  newLessonState: Nullable<LessonState>;
}

type WarningSeverityCounts = { [key in Severity]: number };
type WarningSourceCounts = { [key in WarningInputSource]: WarningSeverityCounts };

export type Alias = string;

/** Add/remove code fold points */
function updateCodePanelFoldPoints(
  foldPoints: Position[],
  foldChange: EditorFoldChange,
): Position[] {
  if (!foldChange.addFold) {
    // Remove an existing fold point
    return foldPoints.filter(p => p.line !== foldChange.position.line);
  }
  if (!foldPoints.find(p => p.line === foldChange.position.line)) {
    // Add a new fold point
    return [...foldPoints, foldChange.position];
  }
  // No change
  return foldPoints;
}

/** Shift code fold points based on added/removed lines */
function adjustCodeFoldPoints(
  foldPoints: Position[],
  foldAdjustment: EditorFoldAdjustment,
): Position[] {
  return foldPoints.map(p => {
    if (p.line >= foldAdjustment.position.line) {
      return {
        ...p,
        line: p.line + foldAdjustment.lineDiff,
      };
    }
    return p;
  });
}

/** Update a CodePanelEditorState instance */
function updateCodePanelState(
  codePanelState: CodePanelEditorState,
  stateChange: CodePanelStateChange,
) {
  switch (stateChange.type) {
    case 'scroll':
      codePanelState.scrollInfo = stateChange.scrollInfo;
      break;
    case 'fold':
      codePanelState.codeFoldPoints = updateCodePanelFoldPoints(
        codePanelState.codeFoldPoints,
        stateChange,
      );
      break;
    case 'fold-adjust':
      codePanelState.codeFoldPoints = adjustCodeFoldPoints(
        codePanelState.codeFoldPoints,
        stateChange,
      );
      break;
    default:
      unreachableCase(stateChange);
  }
}

/** Update the code state within the current asset */
function updateAssetCodeState(
  assetsPanelState: AssetsPanelEditorState,
  stateChange: CodePanelStateChange,
) {
  if (assetsPanelState.selectedAssetIndex === null) {
    return;
  }
  assetsPanelState.assetsState[assetsPanelState.selectedAssetIndex] =
    assetsPanelState.assetsState[assetsPanelState.selectedAssetIndex] || getCodePanelDefaultState();
  updateCodePanelState(
    assetsPanelState.assetsState[assetsPanelState.selectedAssetIndex],
    stateChange,
  );
}

function removeDeletedAssetCodeState(
  assetsPanelState: AssetsPanelEditorState,
  deletedAssetIndex: number,
) {
  const resultState: AssetsPanelEditorState = {
    selectedAssetIndex: assetsPanelState.selectedAssetIndex,
    assetsState: {},
  };
  Object.entries(assetsPanelState.assetsState).forEach(([key, value]) => {
    const intKey = parseInt(key, 10);
    if (intKey < deletedAssetIndex) {
      resultState.assetsState[intKey] = value;
    } else if (intKey > deletedAssetIndex) {
      resultState.assetsState[intKey - 1] = value;
    } else {
      // intKey === deletedAssetIndex
      // Deleted the asset, exclude corresponding state
    }
  });
  assetsPanelState.assetsState = resultState.assetsState;
}

/** Update an AssetsPanelEditorState instance */
function updateAssetsPanelState(
  assetsPanelState: AssetsPanelEditorState,
  stateChange: AssetsPanelStateChange,
) {
  switch (stateChange.type) {
    case 'asset-index':
      assetsPanelState.selectedAssetIndex = stateChange.assetIndex;
      break;
    case 'asset-code':
      updateAssetCodeState(assetsPanelState, stateChange.codeChange);
      break;
    case 'asset-delete':
      removeDeletedAssetCodeState(assetsPanelState, stateChange.assetIndex);
      break;
    default:
      unreachableCase(stateChange);
  }
}

/** Convert a partial ErrorsDict to a full ErrorsDict */
function errorsDict(errors: Partial<ErrorsDict>): ErrorsDict {
  return {
    requestErrors: [],
    elmerErrors: null,
    ...errors,
  };
}

const INITIAL_STATE: LessonEditorState = {
  lessonIsSaving: false,
  lessonIsAutosaving: false,
  requestErrors: [],
  elmerErrors: null,
  editorWarnings: {},
  autosaveOutdated: false,
  mutableLessonSource: BLANK_LESSON_SOURCE,
  transpilerSha: '',
  editorWasDeployed: false,
  status: null,
  interactivesManifest: {},
  camperdownAsPractice: {},
  usedAssets: [],
  mainElm: '',
  additionalElmMains: [],
  additionalElm: {},
  packages: [],
  contentHashes: null,
  lessonWidgetSchema: [],
  unifiedTranspilerWarnings: [],
  editorOptions: null,

  ownerChanged: false,
  newOwnerName: null,

  lessonStateChanged: false,
  newLessonState: null,

  editorState: {
    camperdownState: getCamperdownSourceDefaultState(),
    assetsState: { selectedAssetIndex: null, assetsState: {} },
    compiledElmState: getCodePanelDefaultState(),
  },
  assetPreviewWidth: null,
  forcedReadOnly: false,
};

interface ExtendedRootState extends RootState {
  globals: GlobalState;
}

const lessonModule: Module<LessonEditorState, ExtendedRootState> = {
  namespaced: true,
  state: INITIAL_STATE,

  mutations: {
    setAutosaveOutdated(state: LessonEditorState, value: boolean) {
      state.autosaveOutdated = value;
    },
    setNewOwner(state: LessonEditorState, value: Nullable<string>) {
      state.ownerChanged = value !== null;
      state.newOwnerName = value;
    },
    setNewLessonState(state: LessonEditorState, value: Nullable<LessonState>) {
      state.lessonStateChanged = value !== null;
      state.newLessonState = value;
    },
    isSaving(state: LessonEditorState, value: boolean) {
      state.lessonIsSaving = value;
    },
    isAutosaving(state: LessonEditorState, value: boolean) {
      state.lessonIsAutosaving = value;
    },
    setErrors(state: LessonEditorState, value: ErrorsDict) {
      state.requestErrors = value.requestErrors;
      state.elmerErrors = value.elmerErrors;
    },
    resetErrors(state: LessonEditorState) {
      state.requestErrors = [];
      state.elmerErrors = null;
    },
    setEditorWarnings(state: LessonEditorState, value: EditorWarnings) {
      state.editorWarnings = value;
    },
    setLessonSource(state: LessonEditorState, lessonSource: LessonSourceObject) {
      state.mutableLessonSource = lessonSource;
    },
    setEditorWasDeployed(state: LessonEditorState) {
      state.editorWasDeployed = true;
    },
    setEditorOptions(state: LessonEditorState, options: Nullable<OptionsLocalStorage>) {
      state.editorOptions = options;
    },
    setStatus(state: LessonEditorState, status: EditorStatusTuple) {
      state.status = status;
    },
    setTranspilerResponse(state: LessonEditorState, transpilerResponse: TranspilerResponse) {
      state.interactivesManifest = transpilerResponse.interactivesManifest;
      state.camperdownAsPractice = transpilerResponse.camperdownAsPractice;
      state.mainElm = transpilerResponse.mainElm;
      state.additionalElmMains = transpilerResponse.additionalMains;
      state.additionalElm = transpilerResponse.additionalElm;
      state.packages = transpilerResponse.packages;
      state.contentHashes = transpilerResponse.contentHashes;
      state.lessonWidgetSchema = transpilerResponse.lessonWidgetSchema;
      state.unifiedTranspilerWarnings = transpilerResponse.unifiedWarnings;
      state.usedAssets = transpilerResponse.usedAssets;
    },
    clearEditorState(state: LessonEditorState) {
      state.lessonIsSaving = INITIAL_STATE.lessonIsSaving;
      state.lessonIsAutosaving = INITIAL_STATE.lessonIsAutosaving;
      state.requestErrors = INITIAL_STATE.requestErrors;
      state.elmerErrors = INITIAL_STATE.elmerErrors;
      state.autosaveOutdated = INITIAL_STATE.autosaveOutdated;
      state.mutableLessonSource = INITIAL_STATE.mutableLessonSource;
    },
    setEditorState(state: LessonEditorState, tmpState: EditorState) {
      state.editorState = tmpState;
    },
    setAssetPreviewWidth(state: LessonEditorState, assetPreviewWidth: string) {
      state.assetPreviewWidth = assetPreviewWidth;
    },
    setReadOnly(state: LessonEditorState) {
      state.forcedReadOnly = true;
    },
  },

  actions: {
    resetErrors({ commit }) {
      commit('resetErrors');
    },

    setRequestErrors({ commit }, errors: string[]) {
      const newErrorsDict: ErrorsDict = {
        requestErrors: errors,
        elmerErrors: null,
      };
      commit('setErrors', newErrorsDict);
    },

    async createLesson(
      { commit },
      { params, formData }: LessonCreateApiInputs,
    ): Promise<LessonCreateApiResult> {
      commit('isSaving', true);
      const route = apiResolve({ params, name: 'lesson_create' });
      const [success, result, errors] = await apiRequestWithErrors(route.href, formData);
      const lesson: Optional<Lesson> = result?.lesson;
      const lesson_slug: Optional<string> = result?.lesson_slug;
      commit('isSaving', false);
      if (lesson && lesson_slug) {
        return {
          success,
          errors,
          data: { lesson, lesson_slug },
        };
      }
      return { success, errors };
    },

    async autosaveCreate(
      { commit, rootState },
      { params, formData }: LessonCreateAutosaveApiInputs,
    ): Promise<LessonCreateAutosaveApiResult> {
      commit('isAutosaving', true);

      const route = apiResolve({ params, name: 'lesson_autosave_create' });
      const [success, result, errors] = await apiRequestWithErrors(route.href, formData);
      const autosave: Optional<LessonAutosave> = result?.autosave;
      const transpilerSha: Optional<string> = result?.transpiler_sha;

      commit('setAutosaveOutdated', result?.autosave_outdated?.value === true);

      // New owner is returned as a 400, want to suppress default error messages
      if (result?.ownership_changed) {
        const newOwnerName = result?.ownership_changed?.new_owner_name;
        commit(
          'setNewOwner',
          typeof newOwnerName === 'string' && newOwnerName.length ? newOwnerName : null,
        );
        commit('setErrors', errorsDict({}));
      } else if (result?.lesson_state_not_editable) {
        const newLessonState = result?.lesson_state_not_editable?.new_lesson_state;
        commit(
          'setNewLessonState',
          typeof newLessonState === 'string' && newLessonState.length ? newLessonState : null,
        );
        commit('setErrors', errorsDict({}));
      } else {
        commit('setErrors', errorsDict({ requestErrors: errors }));
      }

      if (autosave) {
        commit('lessonLastAutosave/setValue', autosave, { root: true });
      }

      // Check if the editor was deployed since the last autosave
      if (transpilerSha && (rootState as any)?.transpilerSha.transpilerSha !== transpilerSha) {
        commit('setEditorWasDeployed');
      }

      commit('isAutosaving', false);
      if (autosave && transpilerSha) {
        return {
          success,
          data: { autosave, transpilerSha },
        };
      }
      return { success };
    },

    async draftCreate(
      { commit },
      { params, formData }: LessonDraftCreateApiInputs,
    ): Promise<LessonDraftCreateApiResult> {
      commit('isSaving', true);
      const route = apiResolve({ params, name: 'lesson_draft_create' });
      const [success, result] = await apiRequest(commit, route.href, formData);
      const lesson = result?.lesson;
      if (lesson) {
        commit('lesson/setValue', lesson, { root: true });
      }
      commit('isSaving', false);
      return { success, data: lesson };
    },

    async draftUpdate(
      { commit },
      { params, formData }: LessonDraftUpdateApiInputs,
    ): Promise<LessonDraftUpdateApiResult> {
      commit('isSaving', true);
      const route = apiResolve({ params, name: 'lesson_draft_update' });
      const [success, result] = await apiRequest(commit, route.href, formData);
      const lesson = result?.lesson;
      if (lesson) {
        commit('lesson/setValue', lesson, { root: true });
      }
      commit('isSaving', false);
      return { success, data: lesson };
    },

    async draftPublish(
      { commit },
      { params, formData }: LessonPublishApiInputs,
    ): Promise<LessonPublishApiResult> {
      commit('isSaving', true);
      const route = apiResolve({ params, name: 'lesson_draft_publish' });
      const [success, result] = await apiRequest(commit, route.href, formData);
      const lesson = result?.lesson;
      if (lesson) {
        commit('lesson/setValue', lesson, { root: true });
      }
      commit('isSaving', false);
      return { success, data: lesson };
    },

    async retireVersion(
      { commit },
      { params, formData }: LessonRetireVersionInputs,
    ): Promise<LessonRetireVersionResult> {
      commit('isSaving', true);
      const route = apiResolve({ params, name: 'lesson_retire_version' });
      const [success, result] = await apiRequest(commit, route.href, formData);
      const lesson = result?.lesson;
      if (lesson) {
        commit('lesson/setValue', lesson, { root: true });
      }
      commit('isSaving', false);
      return { success, data: lesson };
    },

    async fetchPreviousVersions(
      { commit, rootState },
      { params }: LessonVersionsApiInputs,
    ): Promise<LessonVersionsApiResult> {
      const route = apiResolve({ params, name: 'lesson_versions_api' });
      const [success, result] = await apiRequest(commit, route.href, {});
      return { success, data: result?.lesson_versions };
    },

    async editHistory(
      { commit },
      { params }: LessonEditHistoryInputs,
    ): Promise<LessonEditHistoryResult> {
      const route = apiResolve({ params, name: 'lesson_edit_history' });
      const [success, result] = await apiRequest(commit, route.href, {});
      return { success, data: result?.edit_history };
    },

    async uploadAssets(
      { commit },
      { data }: LessonUploadAssetsInputs,
    ): Promise<LessonUploadAssetsResult> {
      const route = apiResolve({ name: 'lesson_upload_assets' });
      const [success, result] = await apiRequest(commit, route.href, data);
      return { success, data: result?.uploaded_assets || [] };
    },

    async loadReports(
      { commit },
      { params, query }: LessonReportLoadApiInputs,
    ): Promise<LessonReportLoadApiResult> {
      const route = apiResolve({ params, query, name: 'lesson_reports' });
      try {
        const response = await getData(route.href);
        return { success: true, data: response?.results };
      } catch (e) {
        return { success: false };
      }
    },

    async loadFeedbacks(
      { commit },
      { params, query }: LessonFeedbackLoadApiInputs,
    ): Promise<LessonFeedbackLoadApiResult> {
      const route = apiResolve({ params, query, name: 'lesson_feedback_load' });
      try {
        const response = await getData(route.href);
        return { success: true, data: response?.results };
      } catch (e) {
        return { success: false };
      }
    },

    resetAutosaveOutdated({ commit }) {
      commit('setAutosaveOutdated', false);
    },

    resetNewOwner({ commit }) {
      commit('setNewOwner', null);
    },

    resetNewLessonState({ commit }) {
      commit('setNewLessonState', null);
    },

    setLessonSource({ commit }, lessonSource: LessonSourceObject) {
      commit('setLessonSource', lessonSource);
    },

    clearEditorState({ commit }) {
      commit('clearEditorState');
    },

    setCamperdown({ commit, state }, camperdown: string) {
      const lessonSourceCopy = deepCopyObj(state.mutableLessonSource) as LessonSourceObject;
      lessonSourceCopy.content.camperdown = camperdown;
      commit('setLessonSource', lessonSourceCopy);
    },

    insertCamperdown({ commit, state }, camperdownSnippet: string[]) {
      const lessonSourceCopy = deepCopyObj(state.mutableLessonSource) as LessonSourceObject;
      const cursorLine = state.editorState.camperdownState.cursorPosition.line;
      const oldCamperdown = lessonSourceCopy.content.camperdown.split('\n');
      const newCamperdown = oldCamperdown
        .slice(0, cursorLine)
        .concat(...camperdownSnippet)
        .concat(...oldCamperdown.slice(cursorLine));

      lessonSourceCopy.content.camperdown = newCamperdown.join('\n');
      commit('setLessonSource', lessonSourceCopy);
    },

    setAssets({ commit, state }, assets: LessonAsset[]) {
      const lessonSourceCopy = deepCopyObj(state.mutableLessonSource) as LessonSourceObject;
      lessonSourceCopy.content.assets = assets;
      state.mutableLessonSource = lessonSourceCopy;
      commit('setLessonSource', lessonSourceCopy);
    },

    setStatus({ commit }, status: EditorStatusTuple) {
      commit('setStatus', status);
    },

    setTemporaryStatus({ commit, getters }, [statusText, statusType]: EditorStatusTuple) {
      commit('setStatus', [statusText, statusType]);
      setTimeout(() => {
        if (getters.editorStatusText === statusText) {
          commit('setStatus', null);
        }
      }, 5000);
    },

    clearStatus({ commit }) {
      commit('setStatus', null);
    },

    setTranspilerResponse({ commit }, transpilerResponse: TranspilerResponse) {
      commit('setTranspilerResponse', transpilerResponse);
    },

    initOptions({ commit }) {
      commit('setEditorOptions', getOptionsFromLocalStorage());
    },

    saveOptions({ commit }, options: OptionsLocalStorage) {
      saveOptionsToLocalStorage(options);
      commit('setEditorOptions', options);
    },

    assetAction({ dispatch, state }, inputs: LessonAssetActionInputs) {
      const currentCount = state.mutableLessonSource.content.assets.length;
      switch (inputs.action) {
        case 'add':
          dispatch('setAssets', [...state.mutableLessonSource.content.assets, ...inputs.data]);
          break;
        case 'remove':
          if (!(inputs.index >= 0 && inputs.index < currentCount)) {
            throw new Error(
              `Attempted to remove an asset at index ${inputs.index} ` +
                `that does not exist (max index: ${currentCount - 1})`,
            );
          }
          dispatch('setAssets', [
            ...state.mutableLessonSource.content.assets.slice(0, inputs.index),
            ...state.mutableLessonSource.content.assets.slice(inputs.index + 1),
          ]);
          break;
        case 'update':
          if (!(inputs.index >= 0 && inputs.index < currentCount)) {
            throw new Error(
              `Attempted to update an asset at index ${inputs.index} ` +
                `that does not exist (max index: ${currentCount - 1})`,
            );
          }
          dispatch('setAssets', [
            ...state.mutableLessonSource.content.assets.slice(0, inputs.index),
            inputs.data,
            ...state.mutableLessonSource.content.assets.slice(inputs.index + 1),
          ]);
          break;
        case 'duplicate':
          if (!(inputs.index >= 0 && inputs.index < currentCount)) {
            throw new Error(
              `Attempted to duplicate an asset at index ${inputs.index} ` +
                `that does not exist (max index: ${currentCount - 1})`,
            );
          }
          const asset = state.mutableLessonSource.content.assets[inputs.index];
          const duplicateAsset = deepCopyObj(asset);
          duplicateAsset.alias = toUniqueAssetAlias(
            asset.alias,
            state.mutableLessonSource.content.assets,
          );
          dispatch('setAssets', [
            ...state.mutableLessonSource.content.assets.slice(0, inputs.index),
            asset,
            duplicateAsset,
            ...state.mutableLessonSource.content.assets.slice(inputs.index + 1),
          ]);
          break;
        default:
          unreachableCase(inputs);
      }
    },

    addEditorWarnings({ commit, state }, warningInfo: EditorWarningPayload) {
      const warnings = deepCopyObj(state.editorWarnings) as EditorWarnings;
      warnings[warningInfo.assetAlias] = warnings[warningInfo.assetAlias] || [];

      // Remove duplicate warnings
      warningInfo.warnings.forEach(warning => {
        if (
          !warnings[warningInfo.assetAlias].find(
            w => editorWarningToPlainText(warning) === editorWarningToPlainText(w),
          )
        ) {
          warnings[warningInfo.assetAlias].push(warning);
        }
      });
      commit('setEditorWarnings', warnings);
    },

    clearEditorWarnings({ commit, state }, assetAlias: string) {
      const warnings = deepCopyObj(state.editorWarnings) as EditorWarnings;
      warnings[assetAlias] = [];
      commit('setEditorWarnings', warnings);
    },

    stateChanged({ commit, state }, action: EditorStateAction) {
      const editorStateCopy = deepCopyObj(state.editorState) as EditorState;
      switch (action.type) {
        case 'lesson':
          if (action.change.type === 'cursor-adjust') {
            editorStateCopy.camperdownState.cursorPosition = action.change.position;
          } else {
            updateCodePanelState(editorStateCopy.camperdownState, action.change);
          }
          break;
        case 'compiledElmMain':
          updateCodePanelState(editorStateCopy.compiledElmState, action.change);
          break;
        case 'assets':
          updateAssetsPanelState(editorStateCopy.assetsState, action.change);
          break;
        default:
          unreachableCase(action);
      }

      commit('setEditorState', editorStateCopy);
    },

    assetPreviewWidthChanged({ commit }, assetPreviewWidth: string) {
      commit('setAssetPreviewWidth', assetPreviewWidth);
    },

    markReadOnly({ commit }) {
      commit('setReadOnly');
      commit('lesson/setReadOnly', null, { root: true });
    },
  },

  getters: {
    interactivesManifest(state: LessonEditorState): InteractivesManifest {
      return state.interactivesManifest;
    },
    camperdownAsPractice(state: LessonEditorState): any {
      return state.camperdownAsPractice;
    },
    lessonEditorRequestErrors(state: LessonEditorState): string[] {
      return state.requestErrors;
    },
    lessonSource(state: LessonEditorState): LessonSourceObject {
      return state.mutableLessonSource;
    },
    lessonAssets(state: LessonEditorState): LessonAsset[] {
      return state.mutableLessonSource.content.assets.map(asset => {
        if (isJSONConfigAsset(asset)) {
          // JSON used to be stored as an object instead of a stringified object
          // This was changed on 12/23/2020 -- we don't need to keep this check forever
          return {
            ...asset,
            json: typeof asset.json !== 'string' ? JSON.stringify(asset.json) : asset.json,
          };
        }
        return asset;
      });
    },
    usedAssetAliasesMap(state: LessonEditorState): Map<Alias, number[]> {
      const aliasUsageMap: Map<Alias, number[]> = new Map();
      for (const usedAsset of state.usedAssets) {
        const usages = aliasUsageMap.get(usedAsset.alias) ?? [];
        usages.push(usedAsset.line);
        aliasUsageMap.set(usedAsset.alias, usages);
      }
      for (const list of aliasUsageMap.values()) {
        list.sort((a, b) => a - b);
      }
      return aliasUsageMap;
    },
    editorStatusText(state: LessonEditorState): string | null {
      return state.status ? state.status[0] : null;
    },
    editorStatusType(state: LessonEditorState): string | null {
      return state.status ? state.status[1].toLowerCase() : null;
    },
    getCompiledElm(state: LessonEditorState): CompiledElmGetter {
      return (route: Route) => {
        return {
          elm_main_module: mainModuleName(route),
          additional_main_modules: state.additionalElmMains,
          elm_files: elmFilesForElmer(state.mainElm, state.additionalElm, route),
        };
      };
    },

    camperdownWarnings(state: LessonEditorState): TranspilerWarning[] {
      return state.unifiedTranspilerWarnings.filter(w => w.sourceType === 'camperdown');
    },

    shouldCurrentWarningsBlockPublishing(state: LessonEditorState, getters): boolean {
      const hadBlockingEditorWarnings = Object.values(state.editorWarnings)
        .flat()
        .some(shouldWarningBlockPublishing);
      const hasBlockingTranspilerWarnings =
        state.unifiedTranspilerWarnings.find(w => shouldWarningBlockPublishing(w)) !== undefined;
      return hadBlockingEditorWarnings || hasBlockingTranspilerWarnings;
    },

    getTranspilerAssetWarnings(state: LessonEditorState): TranspilerWarningGetter {
      return (assetAlias: string) => {
        return state.unifiedTranspilerWarnings.filter(
          w => w.sourceType === 'asset' && w.alias === assetAlias,
        );
      };
    },

    getEditorWarnings(state: LessonEditorState): EditorWarningGetter {
      return (assetAlias: string) => {
        return state.editorWarnings[assetAlias] || [];
      };
    },

    getCategorizedWarningCount(state: LessonEditorState): WarningCountsGetter {
      const initialCountItem = () => ({ error: 0, warning: 0, info: 0, debug: 0 });
      const initialCounts: WarningSourceCounts = {
        camperdown: initialCountItem(),
        asset: initialCountItem(),
        settings: initialCountItem(),
      };
      // Count warnings for each sourceType
      const countsBySourceTypeBySeverity: WarningSourceCounts =
        state.unifiedTranspilerWarnings.reduce((accumulator, warning) => {
          switch (warning.severity) {
            case 'error':
              accumulator[warning.sourceType].error += 1;
              break;
            case 'warning':
              accumulator[warning.sourceType].warning += 1;
              break;
            case 'info':
              accumulator[warning.sourceType].info += 1;
              break;
            case 'debug':
              accumulator[warning.sourceType].debug += 1;
              break;
            default:
              unreachableCase(warning.severity);
          }
          return accumulator;
        }, initialCounts);

      // Include editor warnings under sourceType === 'asset'
      Object.values(state.editorWarnings).forEach(warnings => {
        warnings.forEach(warning => {
          switch (warning.severity) {
            case 'error':
              countsBySourceTypeBySeverity.asset.error += 1;
              break;
            case 'warning':
              countsBySourceTypeBySeverity.asset.warning += 1;
              break;
            case 'info':
              countsBySourceTypeBySeverity.asset.info += 1;
              break;
            case 'debug':
              countsBySourceTypeBySeverity.asset.debug += 1;
              break;
            default:
              unreachableCase(warning.severity);
          }
        });
      });

      return (sourceType: WarningInputSource, severity: Severity) =>
        countsBySourceTypeBySeverity[sourceType][severity];
    },

    getCategorizedAssetWarningCount(state: LessonEditorState): AssetWarningCountsGetter {
      const initialCounts = () => ({ error: 0, warning: 0, info: 0, debug: 0 });
      const countsByAliasBySeverity: { [key: string]: WarningSeverityCounts } = {};

      // Count transpiler warnings
      state.unifiedTranspilerWarnings.forEach(warning => {
        if (warning.sourceType === 'asset') {
          countsByAliasBySeverity[warning.alias] =
            countsByAliasBySeverity[warning.alias] || initialCounts();
          switch (warning.severity) {
            case 'error':
              countsByAliasBySeverity[warning.alias].error += 1;
              break;
            case 'warning':
              countsByAliasBySeverity[warning.alias].warning += 1;
              break;
            case 'info':
              countsByAliasBySeverity[warning.alias].info += 1;
              break;
            case 'debug':
              countsByAliasBySeverity[warning.alias].debug += 1;
              break;
            default:
              unreachableCase(warning.severity);
          }
        }
      });

      // Count editor warnings
      Object.entries(state.editorWarnings).forEach(([alias, warnings]) => {
        if (warnings.length) {
          countsByAliasBySeverity[alias] = countsByAliasBySeverity[alias] || initialCounts();
          warnings.forEach(warning => {
            switch (warning.severity) {
              case 'error':
                countsByAliasBySeverity[alias].error += 1;
                break;
              case 'warning':
                countsByAliasBySeverity[alias].warning += 1;
                break;
              case 'info':
                countsByAliasBySeverity[alias].info += 1;
                break;
              case 'debug':
                countsByAliasBySeverity[alias].debug += 1;
                break;
              default:
                unreachableCase(warning.severity);
            }
          });
        }
      });

      return (assetAlias: string, severity: Severity) => {
        const counts = countsByAliasBySeverity[assetAlias];
        return counts !== undefined ? counts[severity] : 0;
      };
    },

    renderMathInLivePreviewOption(state: LessonEditorState): boolean {
      return state.editorOptions?.renderMathInLivePreview === true;
    },
    isSidebarExpanded(state: LessonEditorState): boolean {
      return state.editorOptions?.isSidebarExpanded === true;
    },
    currentSidebarTab(state: LessonEditorState): SidebarTab {
      return state.editorOptions?.currentSidebarTab || 'live_preview';
    },
    getDiagrammarCodePanelState(
      state: LessonEditorState,
    ): (selectedAssetIndex: number) => CodePanelEditorState {
      return (selectedAssetIndex: number) => {
        return (
          state.editorState.assetsState.assetsState[selectedAssetIndex] ||
          getCodePanelDefaultState()
        );
      };
    },
  },
};

export default lessonModule;
